import { initializeApp, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, type Firestore } from "firebase/firestore";
import type { WebEnv } from "../lib/env";

export interface FirebaseClient {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  appCheck: AppCheck | null;
}

let client: FirebaseClient | null = null;

/**
 * Lazily initializes the Firebase app from validated WebEnv. Browser Firebase
 * config is public configuration; it still always comes from environment
 * variables, never from hardcoded values.
 */
export function getFirebaseClient(env: WebEnv): FirebaseClient {
  if (client) {
    return client;
  }

  const app = initializeApp({
    apiKey: env.firebaseApiKey,
    authDomain: env.firebaseAuthDomain,
    projectId: env.firebaseProjectId,
    appId: env.firebaseAppId,
  });

  const auth = getAuth(app);
  const db = getFirestore(app);

  if (env.useEmulators) {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
  }

  // App Check requires a ReCAPTCHA Enterprise site key configured in the
  // Firebase console for this project. Skipped on emulators entirely.
  let appCheck: AppCheck | null = null;
  if (env.appCheckSiteKey && !env.useEmulators) {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(env.appCheckSiteKey),
      isTokenAutoRefreshEnabled: false,
    });
  }

  client = { app, auth, db, appCheck };
  return client;
}
